import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
    Card, CardBody, CardHeader, Container, Table, 
    Button, Spinner 
} from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { getSubFaqs, deleteFaqs, getFaqDetail } from '../../slices/faq/thunk';
import { selectSubFaqData, selectFaqEditData } from '../../slices/faq/selectors';
import { faqApiResponseSuccess } from '../../slices/faq/action';
import { GET_SUB_FAQS } from '../../slices/faq/actionType';
import SweetAlert from 'react-bootstrap-sweetalert';

const SubFAQs = () => {
    const { parentId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedFaqs, setSelectedFaqs] = useState([]);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [sectionTitle, setSectionTitle] = useState('');

    console.log("Current parentId in SubFAQs:", parentId);

    const { subFaqs, loading } = useSelector(selectSubFaqData);
    const { currentFaq } = useSelector(selectFaqEditData);
    
    // Get parent FAQ details to show the section title
    useEffect(() => {
        if (parentId) {
            dispatch(getFaqDetail(parentId));
        }
    }, [dispatch, parentId]);

    // Update section title when currentFaq changes
    useEffect(() => {
        if (currentFaq?.title?.en) {
            setSectionTitle(currentFaq.title.en);
        }
    }, [currentFaq]);

    // Clear subFaqs when component mounts or parentId changes
    useEffect(() => {
        dispatch(faqApiResponseSuccess(GET_SUB_FAQS, []));
    }, [dispatch, parentId]);

    useEffect(() => {
        if (parentId) {
            console.log("Dispatching getSubFaqs with parentId:", parentId);
            dispatch(getSubFaqs(parentId));
        }
    }, [dispatch, parentId]);

    // Add debug logging for subFaqs
    useEffect(() => {
        console.log("Current subFaqs:", subFaqs);
    }, [subFaqs]);

    const handleSelectFaq = (faqId) => {
        setSelectedFaqs(prev => {
            if (prev.includes(faqId)) {
                return prev.filter(id => id !== faqId);
            }
            return [...prev, faqId];
        });
    };

    const handleDeleteConfirm = () => {
        dispatch(deleteFaqs(selectedFaqs)).then(() => {
            setSelectedFaqs([]);
            setShowDeleteAlert(false);
            dispatch(getSubFaqs(parentId));
        }).catch((error) => {
            console.error('Failed to delete sub FAQs:', error);
            setShowDeleteAlert(false);
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <div className="mb-3">
                        <Button
                            color="soft-primary"
                            onClick={() => navigate('/faq-settings')}
                            className="btn-label"
                        >
                            <i className="ri-arrow-left-s-line label-icon align-middle fs-16 me-2"></i>
                            Back to FAQ List
                        </Button>
                    </div>
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0">
                                {sectionTitle ? `Subsections for ${sectionTitle}` : 'Subsections'}
                            </h4>
                            <div>
                                <Link to={`/subfaq-add/${parentId}`}>
                                    <Button color="primary" className="me-2">
                                        <i className="ri-add-line align-bottom me-1"></i> Add Subsection
                                    </Button>
                                </Link>
                                <Button 
                                    color="danger" 
                                    onClick={() => setShowDeleteAlert(true)}
                                    disabled={selectedFaqs.length === 0}
                                >
                                    <i className="ri-delete-bin-line align-bottom me-1"></i> Delete Selected
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            {loading ? (
                                <div className="text-center">
                                    <Spinner color="primary" />
                                </div>
                            ) : (
                                <Table className="table-nowrap mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={subFaqs?.length > 0 && selectedFaqs.length === subFaqs.length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedFaqs(subFaqs.map(faq => faq._id));
                                                            } else {
                                                                setSelectedFaqs([]);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </th>
                                            <th scope="col">SL No</th>
                                            <th scope="col">Title</th>
                                            <th scope="col">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(subFaqs) && subFaqs.map((faq, index) => (
                                            <tr key={faq._id}>
                                                <td>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={selectedFaqs.includes(faq._id)}
                                                            onChange={() => handleSelectFaq(faq._id)}
                                                        />
                                                    </div>
                                                </td>
                                                <td>{index + 1}</td>
                                                <td>{faq.title || 'No Title'}</td>
                                                <td>
                                                    <Link 
                                                        to={`/subfaq-edit/${faq._id}`}
                                                        className="btn btn-sm btn-soft-primary"
                                                    >
                                                        Edit
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!Array.isArray(subFaqs) || subFaqs.length === 0) && (
                                            <tr>
                                                <td colSpan="4" className="text-center">
                                                    No subsections found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <SweetAlert
                warning
                showCancel
                show={showDeleteAlert}
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="light"
                title="Are you sure?"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setShowDeleteAlert(false)}
            >
                You won't be able to revert this!
            </SweetAlert>
        </React.Fragment>
    );
};

export default SubFAQs; 